<script setup lang="ts">
import { ref } from 'vue'
import api from '@/services/api'
import { readSignupEmail } from '@/signupEmail'

// Signup and login hand the address over via sessionStorage to keep it out of the URL (server logs, analytics).
const knownEmail = readSignupEmail()

const email = ref(knownEmail)
const errors = ref<Record<string, string[]>>({})
const successMessage = ref('')
const inFlight = ref(false)

async function handleResend() {
  errors.value = {}
  successMessage.value = ''
  inFlight.value = true

  try {
    const response = await api.post('/resend-verification/', {
      email: email.value,
    })
    successMessage.value = response.data.message
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: { data?: Record<string, string[]> }
      }
      errors.value = axiosError.response?.data ?? {}
    }
  } finally {
    inFlight.value = false
  }
}
</script>

<template>
  <div class="col-md-6">
    <h1 class="my-3">Email Verification Required</h1>
    <p class="lead" v-if="knownEmail">
      Please check <strong>{{ knownEmail }}</strong> for a confirmation link.
    </p>
    <p class="lead" v-else>
      Please check your account email for a confirmation link.
    </p>
    <p v-if="knownEmail">
      Typo in the address above?
      <router-link :to="{ name: 'signup' }">Sign up again</router-link> with the
      correct one.
    </p>
    <p>
      Don't see an email? It may take a minute to be delivered. If you still
      don&rsquo;t see it after a few minutes, be sure to check your spam or junk
      folder.
    </p>

    <h4>Still missing?</h4>
    <p>
      Enter your email below to have it resent. If you still have not received a
      message after 15 minutes, please
      <a href="mailto:support@stadiamaps.com">reach out to support</a>.
    </p>

    <div v-if="successMessage" class="alert alert-success" role="alert">
      {{ successMessage }}
    </div>

    <form @submit.prevent="handleResend" id="email-verification-form">
      <div class="mb-3">
        <label for="email" class="form-label">Email Address</label>
        <input
          type="email"
          class="form-control"
          id="email"
          v-model="email"
          placeholder="example@example.com"
          required
          :class="{ 'is-invalid': errors.email }"
        />
        <div class="invalid-feedback" v-if="errors.email">
          {{ errors.email[0] }}
        </div>
      </div>

      <button type="submit" class="btn btn-primary" :disabled="inFlight">
        <span
          v-if="inFlight"
          class="spinner-border spinner-border-sm"
          role="status"
        />
        <template v-else>Request New Verification Link</template>
      </button>
    </form>
  </div>
</template>

<style lang="scss" scoped></style>
